import { createContext, useContext, useState, type ReactNode } from "react";

/**
 * Central ⇄ Range view mode — a global lens parallel to ImpactMode
 * (ctg/ops). `central` shows a single value everywhere (today's behavior);
 * `range` reveals the sweep bracket (whiskers, bands, Floor/Ceiling columns)
 * on any surface that has a `complete` scenarios block.
 *
 * Mirrors ImpactModeContext exactly so the two toggles behave identically.
 */

export type RangeMode = "central" | "range";

interface RangeModeContextValue {
  mode: RangeMode;
  setMode: (mode: RangeMode) => void;
}

const RangeModeContext = createContext<RangeModeContextValue | null>(null);

export function RangeModeProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<RangeMode>("central");
  return (
    <RangeModeContext.Provider value={{ mode, setMode }}>
      {children}
    </RangeModeContext.Provider>
  );
}

export function useRangeMode(): RangeModeContextValue {
  const ctx = useContext(RangeModeContext);
  if (!ctx) {
    // Fallback when used outside a provider — always Central.
    return { mode: "central", setMode: () => {} };
  }
  return ctx;
}
